/**
 * Shared order lifecycle state machine definition.
 * Models all valid order status transitions for both client and server.
 *
 * This is the ORDER-LEVEL lifecycle (DB status enum transitions),
 * NOT the client-side survey step navigation (that lives in the client machine).
 */
import { setup } from 'xstate';
import type { OrderStatus } from './order.js';
import type { FailureReason, FailureContext } from './generation.js';

// -- Event types --

export const ORDER_LIFECYCLE_EVENTS = [
  'SAVE_SURVEY',
  'CREATE_CHECKOUT',
  'PAYMENT_SUCCESS',
  'PAYMENT_EXPIRED',
  'START_GENERATION',
  'START_REGENERATION',
  'PLAN_READY',
  'APPROVE_PLAN',
  'REJECT_PLAN',
  'CHARACTER_SHEET_READY',
  'APPROVE_CHARACTER_SHEET',
  'REJECT_CHARACTER_SHEET',
  'GENERATION_COMPLETE',
  'GENERATION_FAILED',
  'REGEN_FAILED_RESTORE',
  'RESET_ORDER',
  'SUBMIT_PRINT_JOB',
  'MARK_SHIPPED',
  'CANCEL',
] as const;

export type OrderLifecycleEventType = (typeof ORDER_LIFECYCLE_EVENTS)[number];

export type OrderLifecycleEvent =
  | { type: 'SAVE_SURVEY' }
  | { type: 'CREATE_CHECKOUT' }
  | { type: 'PAYMENT_SUCCESS' }
  | { type: 'PAYMENT_EXPIRED' }
  | { type: 'START_GENERATION' }
  | { type: 'START_REGENERATION' }
  | { type: 'PLAN_READY' }
  | { type: 'APPROVE_PLAN' }
  | { type: 'REJECT_PLAN' }
  | { type: 'CHARACTER_SHEET_READY' }
  | { type: 'APPROVE_CHARACTER_SHEET' }
  | { type: 'REJECT_CHARACTER_SHEET' }
  | { type: 'GENERATION_COMPLETE' }
  | {
      type: 'GENERATION_FAILED';
      reason?: FailureReason;
      context?: FailureContext;
    }
  | { type: 'REGEN_FAILED_RESTORE' }
  | { type: 'RESET_ORDER' }
  | { type: 'SUBMIT_PRINT_JOB' }
  | { type: 'MARK_SHIPPED' }
  | { type: 'CANCEL' };

// -- Context --

export interface OrderLifecycleContext {
  orderId: string | null;
  failureReason: FailureReason | null;
  failureContext: FailureContext | null;
}

// -- Machine definition --

export const orderLifecycleMachine = setup({
  types: {
    context: {} as OrderLifecycleContext,
    events: {} as OrderLifecycleEvent,
  },
}).createMachine({
  id: 'orderLifecycle',
  initial: 'INIT',
  context: {
    orderId: null,
    failureReason: null,
    failureContext: null,
  },
  states: {
    INIT: {
      on: {
        SAVE_SURVEY: 'STARTED',
        CREATE_CHECKOUT: 'PAYMENT_PENDING',
        CANCEL: 'CANCELLED',
      },
    },
    STARTED: {
      on: {
        SAVE_SURVEY: 'STARTED',
        CREATE_CHECKOUT: 'PAYMENT_PENDING',
        CANCEL: 'CANCELLED',
      },
    },
    PAYMENT_PENDING: {
      on: {
        PAYMENT_SUCCESS: 'PAYMENT_COMPLETED',
        PAYMENT_EXPIRED: 'PAYMENT_FAILED',
        CANCEL: 'CANCELLED',
      },
    },
    PAYMENT_COMPLETED: {
      on: {
        START_GENERATION: 'GENERATING_PLAN',
        CANCEL: 'CANCELLED',
      },
    },
    PAYMENT_FAILED: {
      on: {
        CREATE_CHECKOUT: 'PAYMENT_PENDING',
        CANCEL: 'CANCELLED',
      },
    },
    GENERATING_PLAN: {
      on: {
        PLAN_READY: 'PLAN_REVIEW',
        GENERATION_FAILED: 'GENERATION_FAILED',
        CANCEL: 'CANCELLED',
      },
    },
    PLAN_REVIEW: {
      on: {
        APPROVE_PLAN: 'GENERATING_CHARACTER_SHEET',
        REJECT_PLAN: 'GENERATION_FAILED',
        START_GENERATION: 'GENERATING_PLAN',
        CANCEL: 'CANCELLED',
      },
    },
    GENERATING_CHARACTER_SHEET: {
      on: {
        CHARACTER_SHEET_READY: 'CHARACTER_SHEET_REVIEW',
        GENERATION_FAILED: 'GENERATION_FAILED',
        CANCEL: 'CANCELLED',
      },
    },
    CHARACTER_SHEET_REVIEW: {
      on: {
        APPROVE_CHARACTER_SHEET: 'GENERATING_FINAL',
        REJECT_CHARACTER_SHEET: 'GENERATION_FAILED',
        CANCEL: 'CANCELLED',
      },
    },
    GENERATING_FINAL: {
      on: {
        GENERATION_COMPLETE: 'PREVIEW_READY',
        GENERATION_FAILED: 'GENERATION_FAILED',
        REGEN_FAILED_RESTORE: 'PREVIEW_READY',
        CANCEL: 'CANCELLED',
      },
    },
    GENERATION_FAILED: {
      on: {
        START_GENERATION: 'GENERATING_PLAN',
        RESET_ORDER: 'PAYMENT_COMPLETED',
        CANCEL: 'CANCELLED',
      },
    },
    PREVIEW_READY: {
      on: {
        START_REGENERATION: 'GENERATING_FINAL',
        SUBMIT_PRINT_JOB: 'PRINT_JOB_SUBMITTED',
        RESET_ORDER: 'PAYMENT_COMPLETED',
        CANCEL: 'CANCELLED',
      },
    },
    PRINT_JOB_SUBMITTED: {
      on: {
        MARK_SHIPPED: 'SHIPPED',
        CANCEL: 'CANCELLED',
      },
    },
    SHIPPED: {
      type: 'final',
    },
    CANCELLED: {
      type: 'final',
    },
  },
});

// -- Legacy status mapping --
// Some statuses exist in the DB but are not states in the machine.
// Map them to their equivalent machine state for helper functions.

const LEGACY_STATUS_MAP: Partial<Record<OrderStatus, string>> = {
  GENERATING: 'GENERATING_PLAN',
  GENERATION_COMPLETE: 'PREVIEW_READY',
};

function resolveStatus(status: OrderStatus): string {
  return LEGACY_STATUS_MAP[status] ?? status;
}

// -- Static transition table --
// Derived from the machine definition above. Kept in sync manually.
// This avoids creating XState actors at runtime for simple lookups.

type TransitionMap = Record<
  string,
  Partial<Record<OrderLifecycleEventType, OrderStatus>>
>;

const TRANSITION_TABLE: TransitionMap = {
  INIT: {
    SAVE_SURVEY: 'STARTED',
    CREATE_CHECKOUT: 'PAYMENT_PENDING',
    CANCEL: 'CANCELLED',
  },
  STARTED: {
    SAVE_SURVEY: 'STARTED',
    CREATE_CHECKOUT: 'PAYMENT_PENDING',
    CANCEL: 'CANCELLED',
  },
  PAYMENT_PENDING: {
    PAYMENT_SUCCESS: 'PAYMENT_COMPLETED',
    PAYMENT_EXPIRED: 'PAYMENT_FAILED',
    CANCEL: 'CANCELLED',
  },
  PAYMENT_COMPLETED: {
    START_GENERATION: 'GENERATING_PLAN',
    CANCEL: 'CANCELLED',
  },
  PAYMENT_FAILED: { CREATE_CHECKOUT: 'PAYMENT_PENDING', CANCEL: 'CANCELLED' },
  GENERATING_PLAN: {
    PLAN_READY: 'PLAN_REVIEW',
    GENERATION_FAILED: 'GENERATION_FAILED',
    CANCEL: 'CANCELLED',
  },
  PLAN_REVIEW: {
    APPROVE_PLAN: 'GENERATING_CHARACTER_SHEET',
    REJECT_PLAN: 'GENERATION_FAILED',
    START_GENERATION: 'GENERATING_PLAN',
    CANCEL: 'CANCELLED',
  },
  GENERATING_CHARACTER_SHEET: {
    CHARACTER_SHEET_READY: 'CHARACTER_SHEET_REVIEW',
    GENERATION_FAILED: 'GENERATION_FAILED',
    CANCEL: 'CANCELLED',
  },
  CHARACTER_SHEET_REVIEW: {
    APPROVE_CHARACTER_SHEET: 'GENERATING_FINAL',
    REJECT_CHARACTER_SHEET: 'GENERATION_FAILED',
    CANCEL: 'CANCELLED',
  },
  GENERATING_FINAL: {
    GENERATION_COMPLETE: 'PREVIEW_READY',
    GENERATION_FAILED: 'GENERATION_FAILED',
    REGEN_FAILED_RESTORE: 'PREVIEW_READY',
    CANCEL: 'CANCELLED',
  },
  GENERATION_FAILED: {
    START_GENERATION: 'GENERATING_PLAN',
    RESET_ORDER: 'PAYMENT_COMPLETED',
    CANCEL: 'CANCELLED',
  },
  PREVIEW_READY: {
    START_REGENERATION: 'GENERATING_FINAL',
    SUBMIT_PRINT_JOB: 'PRINT_JOB_SUBMITTED',
    RESET_ORDER: 'PAYMENT_COMPLETED',
    CANCEL: 'CANCELLED',
  },
  PRINT_JOB_SUBMITTED: { MARK_SHIPPED: 'SHIPPED', CANCEL: 'CANCELLED' },
  SHIPPED: {},
  CANCELLED: {},
};

// -- Helper functions --

/**
 * Check if a transition from the given status with the given event is valid.
 */
export function canTransition(
  currentStatus: OrderStatus,
  event: OrderLifecycleEventType
): boolean {
  return getNextStatus(currentStatus, event) !== null;
}

/**
 * Get all valid event types that can be sent from the given status.
 */
export function getValidEvents(
  currentStatus: OrderStatus
): OrderLifecycleEventType[] {
  const resolved = resolveStatus(currentStatus);
  const transitions = TRANSITION_TABLE[resolved];
  if (!transitions) return [];

  return ORDER_LIFECYCLE_EVENTS.filter((eventType) => {
    const target = transitions[eventType];
    // Filter out self-transitions (like SAVE_SURVEY in STARTED)
    return target !== undefined && target !== resolved;
  });
}

/**
 * Get the next status after applying an event to the current status.
 * Returns null if the transition is invalid.
 */
export function getNextStatus(
  currentStatus: OrderStatus,
  event: OrderLifecycleEventType
): OrderStatus | null {
  const resolved = resolveStatus(currentStatus);
  const transitions = TRANSITION_TABLE[resolved];
  if (!transitions) return null;

  const target = transitions[event];
  if (target === undefined) return null;

  // Self-transitions return null (no state change)
  if (target === resolved) return null;

  return target;
}
